import React, { useState, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { withStyles, Tooltip, IconButton, Typography } from '@material-ui/core';
import { hasPermission, createHistory, hasAtLeastOnePermission } from '../../sc-utilities';
import AuthServiceList from './AuthServiceList';
import { FindInPage } from '@material-ui/icons';

const styles = theme => ({
    root: {
        width: `100%`,
        height: 'auto',
        paddingLeft: theme.spacing(-1),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    "@global": {
        "[class*='statusActive']": {
            color: theme.palette.success.dark
        },
        "[class*='statusInactive']": {
            color: theme.palette.error.dark
        }
    },
    statusActive: {
        color: theme.palette.success.dark
    },
    statusInactive: {
        color: theme.palette.error.dark
    }
});

const AuthService = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, authServiceID: null, redirectPath: '' });

    let visible = columns && columns['AuthService'];
    const tableColumns = useMemo(() => {
        return [{
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: rowData => <>
                {
                    hasPermission(permissions, 'auth_service_load') ? <Tooltip title={translations["auth_service_details"] || "Authorization Service details"}>
                        <IconButton value={rowData.AuthServiceID} aria-label={translations["auth_service_details"] || "Authorization Service details"} onClick={() => redirectHandler(`/settings/auth-service/edit?authServiceID=${rowData.AuthServiceID}`)}>
                            <FindInPage />
                        </IconButton>
                    </Tooltip> : ''
                }
            </>
        },
        {
            title: translations["name"] || 'Name', field: 'Name',
            hidden: visible ? visible['Name'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Name ?? (translations['short_not_assigned'] || 'n/a')}</Typography>;
            }
        },
        {
            title: translations["base_url"] || 'Base URL', field: 'BaseURL',
            hidden: visible ? visible['BaseURL'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.BaseURL ?? (translations['short_not_assigned'] || 'n/a')}</Typography>;
            }
        },
        {
            title: translations["type"] || 'Type', field: 'Type',
            hidden: visible ? visible['Type'] : false,
            filtering: true,
            lookup: { 'openid': translations['openid'] || 'OpenID' },
            defaultFilter: ['openid'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Type ?? (translations['short_not_assigned'] || 'n/a')}</Typography>;
            }
        },
        {
            title: translations["active"] || 'Active', field: 'Active',
            hidden: visible ? visible['Active'] : false,
            filtering: true,
            lookup: { '1': translations['active'] || 'Active', '0': translations['inactive'] || 'Inactive' },
            defaultFilter: ['1', '0'],
            render: rowData => {
                return <CustomChip key={`active_${rowData.ExternalSystemSettingID}`} className={rowData.Active ? 'statusActive' : 'statusInactive'} value={rowData.Active ? translations['active'] || 'Active' : translations['inactive'] || 'Inactive'} />;
            }
        },
        {
            title: translations["deleted"] || 'Deleted', field: 'Deleted',
            hidden: visible ? visible['Deleted'] : false,
            filtering: true,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Deleted ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        }
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible])
    if (!hasAtLeastOnePermission(permissions, ['auth_service_load'])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, authServiceID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, authServiceID: authServiceID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'auth_service_list') ?
                <AuthServiceList
                    tableColumns={tableColumns}
                    redirectHandler={redirectHandler}
                    tableRef={tableRef}
                />
                :
                null}
        </div>
    );
};

const CustomChip = props => {
    const { keyValue, value, className } = props;

    return (
        <Typography key={keyValue} className={className} variant={'caption'}>{value}</Typography>
    );
};

const mapStateToProps = state => {
    return {
        translations: state?.translate?.translations?.activeTranslations || {},
        columns: state?.core?.login?.userSettings?.Columns || null,
        permissions: state?.core?.login?.permissions || []
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(AuthService));
